import {
    REQUIRED_ERROR,
    EMAIL_ERROR,
    PHONE_ERROR,
    DATE_FORMAT_ERROR,
    PERIOD_ERROR,
    MIN_CHARS_ERROR,
    REQUIRED_VARIANTS_ERROR,
    FORMAT_ERROR,
} from "@/constants/errorFieldValues";
import * as systemTypes from "@/domains/profile/constants/system-types";
import * as customTypes from "@/domains/profile/constants/custom-types";
import { isPhoneValid } from "@/utils/validate/phone";
import { isEmailValid } from "@/utils/validate/email";
import { isDateValid } from "@/utils/date/valid";
import { toDate } from "@/utils/date/to-date";
import { isSnilsValid } from "@/utils/validate/snils";
import { isURL } from "@/utils/validate/url";

function textValidator(field, value) {
    const { required, minLength } = field;
    const text = value && value.value;
    if (!text || text.trim().length==0) {
        return required ? REQUIRED_ERROR : null;
    }
    if (!minLength) return null;
    return text.length < minLength ? MIN_CHARS_ERROR(minLength) : null;
}

function emailValidator(field, value) {
    const { required } = field;
    const email = value && value.value;
    if (!email) {
        return required ? REQUIRED_ERROR : null;
    }
    return isEmailValid(email) ? null : EMAIL_ERROR;
}

function phoneValidator(field, value) {
    const { required } = field;
    const phone = value && value.value;
    if (!phone) {
        return required ? REQUIRED_ERROR : null;
    }
    return isPhoneValid(phone) ? null : PHONE_ERROR;
}

function dateValidator(field, value) {
    const { required } = field;
    const date = value && value.value;
    if (!date) {
        return required ? REQUIRED_ERROR : null;
    }
    return isDateValid(date) ? null : DATE_FORMAT_ERROR;
}

function periodValidator(field, value) {
    const { required } = field;
    const period = value && value.value;
    if (!period) {
        return required ? REQUIRED_ERROR : null;
    }

    const [from, to] = period;

    if (!from || !to) {
        return required ? REQUIRED_ERROR : null;
    }

    const fromDateString = from.trim();
    const toDateString = to.trim();

    if (!fromDateString || !isDateValid(fromDateString) || !toDateString || !isDateValid(toDateString))
        return DATE_FORMAT_ERROR;

    const fromDateObj = toDate(fromDateString);
    const toDateObj = toDate(fromDateString);

    return fromDateObj > toDateObj ? PERIOD_ERROR : null;
}

function listValidator(field, value) {
    const { required } = field;
    if (!required) return null;
    const list = value.value || [];
    return list.length ? null : REQUIRED_VARIANTS_ERROR;
}

function selectValidator(field, value) {
    const { required } = field;
    if (!required) return null;

    const { items, customItems } = value;

    if (items && items.length) return null;
    if (customItems && customItems.length) return null;

    return REQUIRED_ERROR;
}

const systemValidators = {
    [systemTypes.FIELD_TYPE_COMMENT]: textValidator,
    [systemTypes.FIELD_TYPE_FULL_NAME]: textValidator,
    [systemTypes.FIELD_TYPE_EMAIL]: emailValidator,
    [systemTypes.FIELD_TYPE_PHONE]: phoneValidator,
    [systemTypes.FIELD_TYPE_BIRTHDAY]: dateValidator,
    [systemTypes.FIELD_TYPE_CITIZENSHIP]: selectValidator,
    [systemTypes.FIELD_TYPE_WAY_TO_WORK]: selectValidator,
    [systemTypes.FIELD_TYPE_METRO]: selectValidator,
    [systemTypes.FIELD_TYPE_DESIRED_POSITION]: selectValidator,
    [systemTypes.FIELD_TYPE_VACANCY_SOURCE]: selectValidator,
    [systemTypes.FIELD_TYPE_RESIDENTIAL_ADDRESS]: textValidator,
    [systemTypes.FIELD_TYPE_REGISTRATION_ADDRESS]: (field, value) => {
        const { required } = field;
        if (!required) return null;

        const fieldValue = value && value.value;
        if (!fieldValue) return REQUIRED_ERROR;

        const { address } = fieldValue;

        return address ? null : REQUIRED_ERROR;
    },
    [systemTypes.FIELD_TYPE_GENDER]: (field, value) => {
        const { required } = field;
        if (!required) return null;

        const fieldValue = value && value.value;
        if (!fieldValue) return REQUIRED_ERROR;

        return null;
    },
    [systemTypes.FIELD_TYPE_PHOTO]: (field, value) => {
        const { required } = field;
        if (!required) return null;

        const { files, oldFiles } = value;
        if (files && files.length) return null;
        if (oldFiles && oldFiles.length) return null;
        return REQUIRED_ERROR;
    },
    [systemTypes.FIELD_TYPE_AUTO]: (field, value) => {
        const fieldValue = value && value.value;
        if (!fieldValue) return null;
        const { year } = fieldValue;
        if (!year) return null;
        const yearError = year.length === 4 ? null : FORMAT_ERROR;
        return yearError ? { year: yearError } : null;
    },
    [systemTypes.FIELD_TYPE_EXPERIENCE]: (field, value) => {
        const fieldValue = value && value.value;
        if (!fieldValue) return null;
        const { phone } = fieldValue;
        if (!phone) return null;
        const phoneError = isPhoneValid(phone) ? null : PHONE_ERROR;
        return phoneError ? { phone: phoneError } : null;
    },
    [systemTypes.FIELD_TYPE_PASSPORT]: (field, value) => {
        const { required } = field;

        let commonError;
        let seriesError;
        let numberError;
        let issuedByError;
        let issueDateError;

        const fieldValue = value && value.value;
        if (!fieldValue) {
            if (required) commonError = "Необходимо добавить паспортные данные";
        } else {
            const { series, number, issuedBy, issueDate } = fieldValue;

            if (!series && !number && !issuedBy && !issueDate) {
                if (required) commonError = "Необходимо добавить паспортные данные";
            } else {
                seriesError = required && !series ? REQUIRED_ERROR : null;
                numberError = required && !number ? REQUIRED_ERROR : null;
                issuedByError = required && !issuedBy ? REQUIRED_ERROR : null;

                if (issueDate) issueDateError = isDateValid(issueDate) ? null : DATE_FORMAT_ERROR;
                else if (required) issueDateError = REQUIRED_ERROR;
            }
        }

        if (!commonError && !seriesError && !numberError && !issuedByError && !issueDateError) {
            return null;
        }

        return {
            common: commonError,
            series: seriesError,
            number: numberError,
            issuedBy: issuedByError,
            issueDate: issueDateError,
        };
    },
    [systemTypes.FIELD_TYPE_DOCUMENTS]: (field, value) => {
        const { required } = field;
        const fieldValue = value && value.value;
        if (!fieldValue) return null;
        const { snils, inn } = fieldValue;

        let commonError;
        let snilsError;
        let innError;

        if (!snils && !inn) {
            if (required) commonError = "Необходимо добавить документы";
        } else {
            if (!snils) {
                if (required) snilsError = REQUIRED_ERROR;
            } else snilsError = isSnilsValid(snils) ? null : FORMAT_ERROR;

            innError = required && !inn ? REQUIRED_ERROR : null;
        }

        if (!commonError && !snilsError && !innError) return null;

        return { common: commonError, inn: innError, snils: snilsError };
    },
    [systemTypes.FIELD_TYPE_PORTFOLIO]: (field, value) => {
        const { required } = field;
        if (!required && !value.value) return null;
        const {
            files,
            oldFiles,
            value: { links  },
        } = value;

        if (!links) links = [];

        const commonError =
            required && !files.length && !oldFiles.length && !links.length
                ? "Необходимо добавить ссылку или загрузить файл"
                : null;

        const linksErrors = links.map((item) => {
            const { link } = item;
            if (!link) return REQUIRED_ERROR;
            return isURL(link) ? null : FORMAT_ERROR;
        });
        const hasLinksErrors = linksErrors.some(Boolean);

        if (!commonError && !hasLinksErrors) return null;
        return { common: commonError, links: linksErrors };
    },
    [systemTypes.FIELD_TYPE_CAREER]: (field, value) => {
        const { salaryRequired, workStartRequired } = field.career;
        const fieldValue = (value && value.value) || {};
        const { salary, startDate } = fieldValue;

        const salaryError = salaryRequired && !salary ? REQUIRED_ERROR : null;
        let startDateError = null;

        if (!startDate) {
            if (workStartRequired) startDateError = REQUIRED_ERROR;
        } else {
            startDateError = isDateValid(startDate) ? null : DATE_FORMAT_ERROR;
        }

        if (!salaryError && !startDateError) return null;

        return {
            salary: salaryError,
            startDate: startDateError,
        };
    },
    [systemTypes.FIELD_TYPE_ADDITIONAL_PHONE]: (field, value) => {
        const { required } = field;
        const fieldValue = (value && value.value) || {};

        const { phone, name, status } = fieldValue;

        let phoneError = null;
        const nameError = required && !name ? REQUIRED_ERROR : null;
        const statusError = required && !status ? REQUIRED_ERROR : null;

        if (!phone) {
            if (required) phoneError = REQUIRED_ERROR;
        } else {
            phoneError = isPhoneValid(phone) ? null : PHONE_ERROR;
        }

        if (!phoneError && !nameError && !statusError) return null;
        return {
            phone: phoneError,
            name: nameError,
            status: statusError,
        };
    },
    [systemTypes.FIELD_TYPE_PERSONAL_DATA]: (field, value) => {
        const { personalData } = field;
        const fieldValue = (value && value.value) || {};
        const { familyStatus, children } = fieldValue;

        const familyStatusError = personalData.familyStatus.required && !familyStatus ? REQUIRED_ERROR : null;

        const childrenErrors = (children || []).map((child) => {
            const { age } = child;
            return age ? null : { age: REQUIRED_ERROR };
        });

        const hasChildrenErrors = childrenErrors.some(Boolean);

        if (!familyStatusError && !hasChildrenErrors) return null;

        return {
            familyStatus: familyStatusError,
            children: childrenErrors,
        };
    },
    [systemTypes.FIELD_TYPE_EDUCATION]: (field, value) => {
        const { required } = field;
        const list = (value.value && value.value.educations) || [];

        const commonError = required && !list.length ? "Необходимо добавить образование" : null;

        const getYearError = (year) => {
            if (!year) return REQUIRED_ERROR;
            if (year.length !== 4) return FORMAT_ERROR;
            return null;
        };

        const itemsErrors = list.map((item) => {
            const { institution, specialization, from, to } = item;

            const institutionError = institution ? null : REQUIRED_ERROR;
            const specializationError = specialization ? null : REQUIRED_ERROR;

            const fromError = getYearError(from);
            const toError = getYearError(to);
            let periodError = null;

            if (!fromError && !toError) {
                periodError = +from > +to ? "Неверный интервал" : null;
            }

            if ([institutionError, specializationError, fromError, toError, periodError].some(Boolean)) {
                return {
                    institution: institutionError,
                    specialization: specializationError,
                    from: fromError,
                    to: toError,
                    period: periodError,
                };
            }

            return null;
        });

        const hasErrors = itemsErrors.some(Boolean);

        if (!commonError && !hasErrors) return null;

        return {
            common: commonError,
            items: itemsErrors,
        };
    },
    [systemTypes.FIELD_TYPE_RECENT_JOBS]: (field, value) => {
        const { required } = field;
        const fieldValue = value.value || {};
        const list = fieldValue.jobs || [];

        const commonError = required && !list.length ? REQUIRED_ERROR : null;

        const itemsErrors = list.map((item) => {
            const { company, position, period } = item;

            const companyError = company ? null : REQUIRED_ERROR;
            const positionError = position ? null : REQUIRED_ERROR;

            const [from, to] = period;
            const periodError = from && to ? null : REQUIRED_ERROR;

            if ([companyError, positionError, periodError].some(Boolean)) {
                return {
                    company: companyError,
                    position: positionError,
                    period: periodError,
                };
            }

            return null;
        });

        const hasErrors = itemsErrors.some(Boolean);

        if (!commonError && !hasErrors) return null;

        return {
            common: commonError,
            items: itemsErrors,
        };
    },
    [systemTypes.FIELD_TYPE_SKILLS]: (field, value) => {
        const { skills } = field;
        const { program: programsConfig, language: languagesConfig } = skills;

        const fieldValue = value.value || {};
        const { programs: programsList, languages: languagesList } = fieldValue;

        function getSkillError(data) {
            const { level, value } = data;
            const levelError = level ? null : REQUIRED_ERROR;
            const valueError = value ? null : REQUIRED_ERROR;
            if (!levelError && !valueError) return null;
            return {
                level: levelError,
                value: valueError,
            };
        }

        const programsCommonError =
            programsConfig.required && !programsList.length ? "Необходимо добавить программу" : null;
        const languagesCommonError =
            languagesConfig.required && !languagesList.length ? "Необходимо добавить иностранный язык" : null;
        const programsErrors = (programsList || []).map(getSkillError);
        const languagesErrors = (languagesList || []).map(getSkillError);

        const hasProgramsErrors = programsErrors.some(Boolean);
        const hasLanguagesErrors = languagesErrors.some(Boolean);

        if (!programsCommonError && !languagesCommonError && !hasProgramsErrors && !hasLanguagesErrors) return null;

        return {
            programs: {
                common: programsCommonError,
                items: programsErrors,
            },
            languages: {
                common: languagesCommonError,
                items: languagesErrors,
            },
        };

        // const commonError = required && !list.length ? REQUIRED_ERROR : null;

        // const itemsErrors = list.map((item) => {
        //     const { company, position } = item;

        //     const companyError = company ? null : REQUIRED_ERROR;
        //     const positionError = position ? null : REQUIRED_ERROR;

        //     if ([companyError, positionError].some(Boolean)) {
        //         return {
        //             company: companyError,
        //             position: positionError,
        //         };
        //     }

        //     return null;
        // });

        // const hasErrors = itemsErrors.some(Boolean);

        // if (!commonError && !hasErrors) return null;

        // return {
        //     common: commonError,
        //     items: itemsErrors,
        // };
    },
};

const customValidators = {
    [customTypes.FIELD_TYPE_TEXT]: textValidator,
    [customTypes.FIELD_TYPE_NUMBER]: textValidator,
    [customTypes.FIELD_TYPE_DATE]: dateValidator,
    [customTypes.FIELD_TYPE_PERIOD]: periodValidator,
    [customTypes.FIELD_TYPE_CHECKBOXES]: listValidator,
    [customTypes.FIELD_TYPE_RADIO]: listValidator,
    [customTypes.FIELD_TYPE_LIST_SINGLE]: selectValidator,
    [customTypes.FIELD_TYPE_LIST_MULTIPLE]: selectValidator,
    [customTypes.FIELD_TYPE_FILE]: (field, value) => {
        if (!field.required) return null;
        const { files, oldFiles } = value;
        if (files && files.length) return null;
        if (oldFiles && oldFiles.length) return null;
        return "Необходимо загрузить хотя бы один файл";
    },
};

export function validateFieldValue(field, value) {
    const { isSystem, fieldType } = field;
    const validator = isSystem ? systemValidators[fieldType] : customValidators[fieldType];
    const error = typeof validator === "function" ? validator(field, value) : null;

    return {
        isValid: !error,
        error,
    };
}
